/**
 * Show Display Utilities
 *
 * Handles the display logic for show collaborations, adapted from festival B2B patterns.
 * Provides consistent display formatting for show titles with multiple artists.
 */

export interface ShowArtist {
	id: string;
	show_id?: string;
	position: number;
	artist_id?: string;
	time_start?: string;
	time_end?: string;
	is_headliner: boolean;
	role: "headliner" | "collaborator" | "supporting_artist";
	setlist_fm_url?: string | null;
	artist: {
		id: string;
		name: string;
		slug?: string;
		image_url?: string | null;
		spotify_id?: string | null;
		spotify_url?: string | null;
		spotify_genres?: string[] | null;
		manual_genres?: string[] | null;
		youtube_url?: string | null;
		instagram_url?: string | null;
		twitter_url?: string | null;
		website_url?: string | null;
		bio?: string | null;
	};
}

export interface ProcessedShow {
	id: string;
	title: string;
	artist_name: string;
	date: string;
	location: string;
	slug: string;
	presents_name?: string | null;
	is_collaborative?: boolean;
	collaboration_format?: "×" | "x" | "B2B" | "+" | "&" | "vs." | ",";
	collaboration_alias?: string | null;
	show_artists?: ShowArtist[];
	[key: string]: unknown;
}

export interface ShowDisplayParts {
	mainText: string;
	connector: string | null;
	secondaryText: string | null;
	realMainText?: string | null;
	isCollaborative: boolean;
	isPresents: boolean;
	parts?: Array<{ text: string; type: "artist" | "connector" | "presents" }>;
}

/**
 * Gets display parts for show titles with proper collaboration formatting
 *
 * Handles:
 * - Single artist shows: "Deadmau5"
 * - Presents pattern: "UNiiQU3 pres. PBNJ"
 * - Two-artist collaborations: "San Pacho × Andruss"
 * - Multi-artist collaborations: "San Pacho × Andruss × Facu Baez"
 * - Group aliases: "Day Trip in the Park" (for collaborative events)
 */
export function getShowDisplayParts(show: ProcessedShow): ShowDisplayParts {
	// Handle presents pattern first
	if (show.presents_name) {
		return {
			mainText: show.title,
			connector: "pres.",
			secondaryText: show.presents_name,
			isCollaborative: false,
			isPresents: true,
		};
	}

	// Handle non-collaborative shows
	if (
		!show.is_collaborative ||
		!show.show_artists ||
		show.show_artists.length <= 1
	) {
		return {
			mainText: show.collaboration_alias || show.title,
			connector: null,
			secondaryText: null,
			isCollaborative: false,
			isPresents: false,
		};
	}

	// Handle collaborative shows
	const collaborators = show.show_artists
		.filter((sa) => sa.role === "collaborator")
		.sort((a, b) => a.position - b.position);

	if (collaborators.length === 0) {
		// Fallback to show title if no collaborators found
		return {
			mainText: show.collaboration_alias || show.title,
			connector: null,
			secondaryText: null,
			isCollaborative: false,
			isPresents: false,
		};
	}

	// Use group alias if available
	if (show.collaboration_alias) {
		const artistNames = collaborators.map((c) => c.artist.name).join(", ");
		return {
			mainText: show.collaboration_alias,
			connector: null,
			secondaryText: null,
			realMainText: artistNames,
			isCollaborative: true,
			isPresents: false,
		};
	}

	// Handle two-artist collaboration
	if (collaborators.length === 2) {
		const connector = getCollaborationConnector(show.collaboration_format);
		return {
			mainText: collaborators[0]?.artist.name || "",
			connector,
			secondaryText: collaborators[1]?.artist.name || "",
			isCollaborative: true,
			isPresents: false,
		};
	}

	// Handle multi-artist collaboration (3+ artists)
	if (collaborators.length >= 3) {
		const connector = getCollaborationConnector(show.collaboration_format);
		const parts: Array<{ text: string; type: "artist" | "connector" }> = [];

		collaborators.forEach((collab, index) => {
			parts.push({ text: collab.artist.name, type: "artist" });

			// Add connector between artists (but not after the last one)
			if (index < collaborators.length - 1) {
				parts.push({ text: connector, type: "connector" });
			}
		});

		return {
			mainText: collaborators[0]?.artist.name || "", // First artist as main text
			connector: null, // Use parts array for complex display
			secondaryText: null,
			isCollaborative: true,
			isPresents: false,
			parts,
		};
	}

	// Fallback
	return {
		mainText: show.title,
		connector: null,
		secondaryText: null,
		isCollaborative: false,
		isPresents: false,
	};
}

/**
 * Gets the appropriate connector string for collaboration format
 */
function getCollaborationConnector(format?: string): string {
	switch (format) {
		case "×":
		case "x":
			return "×"; // Unicode multiplication sign (preferred)
		case "B2B":
			return "B2B";
		case "+":
			return "+";
		case "&":
			return "&";
		case "vs.":
			return "vs.";
		case ",":
			return ",";
		default:
			return "×"; // Default to multiplication sign
	}
}

/**
 * Gets display text for show artist name without JSX (for use in components)
 * Returns plain text representation for simple displays
 */
export function getShowDisplayText(show: ProcessedShow): string {
	const displayParts = getShowDisplayParts(show);

	// Handle multi-artist collaborations with parts array
	if (displayParts.parts && displayParts.parts.length > 0) {
		return displayParts.parts.map((part) => part.text).join(" ");
	}

	// Handle standard display
	let result = displayParts.mainText;
	if (displayParts.connector) {
		result += ` ${displayParts.connector}`;
	}
	if (displayParts.secondaryText) {
		result += ` ${displayParts.secondaryText}`;
	}

	return result;
}
